/**
 * client/render/glsl.ts — shared GLSL chunks + the shared uniform block of the BO2 lighting model.
 * OWNER: RENDER agent.
 *
 * Lighting model (research/map-hijacked.md "Lightmap", ps_pimp_shader_lmap_471004b6, technique "lit sun shadow"):
 *   A   = page0.rgb / (page0.a + 1e-6);  B = page1.rgb / (page1.a + 1e-6);  dir = page2.rgb * 2 - 1
 *   E   = A + B * saturate(dot(dir, N))                       (N = bumped normal, CoD space)
 *   col = (colorMap * vertexColor)^2 * (E + sunShadow * sunColor * saturate(dot(sunDir, N)))
 *   out = sqrt(lerp(fog, col, fogFactor) * exposure)          -> then the map LUT (post pass)
 * Static/entity models use per-vertex irradiance (_LIGHT * lightmapScale) instead of the lightmap.
 * The scene is rendered into an 8-bit target holding sqrt-encoded colour; post.ts applies the colour LUT.
 *
 * All positions/normals in these shaders are THREE space (world). CoD z == three y; CoD = (x, -z, y).
 */
import * as THREE from 'three';

/** One set of uniform objects referenced by every BO2 material (update once per frame). */
export interface Bo2Uniforms {
  uExposure: THREE.IUniform<number>;
  uSunDir: THREE.IUniform<THREE.Vector3>; //   three space, TO the sun
  uSunColor: THREE.IUniform<THREE.Vector3>; // HDR (meta.lighting.sun.color)
  uFogColor: THREE.IUniform<THREE.Vector3>;
  uSunFogColor: THREE.IUniform<THREE.Vector3>;
  uSunFogDir: THREE.IUniform<THREE.Vector3>;
  uFogA: THREE.IUniform<THREE.Vector4>; // baseDist, 1/halfDist, baseHeight, 1/halfHeight
  uFogB: THREE.IUniform<THREE.Vector4>; // fogOpacity, sunFogOpacity, sunFogInner deg, sunFogOuter deg
  uShadowStatic: THREE.IUniform<THREE.Texture | null>;
  uShadowDyn: THREE.IUniform<THREE.Texture | null>;
  uShadowMatrix: THREE.IUniform<THREE.Matrix4>;
  uShadowParams: THREE.IUniform<THREE.Vector4>; // 1/w, 1/h, enabled, dynEnabled
  uLmPacked: THREE.IUniform<THREE.Texture | null>;
  uLmScale: THREE.IUniform<number>;
  uCullScale: THREE.IUniform<number>;
  uSkyCube: THREE.IUniform<THREE.CubeTexture | null>;
  uSkyRot: THREE.IUniform<THREE.Vector3>;
  uTime: THREE.IUniform<number>;
  /** One dynamic point light (explosion / muzzle flash): xyz three space, w = radius (0 = off). */
  uDynLightPos: THREE.IUniform<THREE.Vector4>;
  uDynLightColor: THREE.IUniform<THREE.Vector3>;
  /** Primary (non-sun) lights, meta.lighting.primaryLights (index = primary light index, MAX_PRIMARY_LIGHTS). */
  uPLPos: THREE.IUniform<THREE.Vector4[]>; //   xyz three space, radius
  uPLDir: THREE.IUniform<THREE.Vector4[]>; //   xyz spot axis (three, pointing away from the light), cosHalfFovOuter (-1 = omni)
  uPLColor: THREE.IUniform<THREE.Vector4[]>; // rgb HDR, cosHalfFovInner
  /** Global primary-light scale (debug toggle; 1 = data values). */
  uPLScale: THREE.IUniform<number>;
}

export const MAX_PRIMARY_LIGHTS = 24;

export function createBo2Uniforms(): Bo2Uniforms {
  return {
    uExposure: { value: 0.2 },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunColor: { value: new THREE.Vector3(1, 1, 1) },
    uFogColor: { value: new THREE.Vector3(0.5, 0.6, 0.7) },
    uSunFogColor: { value: new THREE.Vector3(1, 0.7, 0.5) },
    uSunFogDir: { value: new THREE.Vector3(1, 0, 0) },
    uFogA: { value: new THREE.Vector4(200, 1 / 36000, -100, 1 / 10000) },
    uFogB: { value: new THREE.Vector4(0, 0, 0, 90) },
    uShadowStatic: { value: null },
    uShadowDyn: { value: null },
    uShadowMatrix: { value: new THREE.Matrix4() },
    uShadowParams: { value: new THREE.Vector4(1 / 4096, 1 / 4096, 0, 0) },
    uLmPacked: { value: null },
    uLmScale: { value: 4 },
    uCullScale: { value: 1 },
    uSkyCube: { value: null },
    uSkyRot: { value: new THREE.Vector3(-0.6427876949310303, -0.7660443782806396, 0.6427876949310303) },
    uTime: { value: 0 },
    uDynLightPos: { value: new THREE.Vector4(0, 0, 0, 0) },
    uDynLightColor: { value: new THREE.Vector3(0, 0, 0) },
    uPLPos: { value: Array.from({ length: MAX_PRIMARY_LIGHTS }, () => new THREE.Vector4(0, 0, 0, 0)) },
    uPLDir: { value: Array.from({ length: MAX_PRIMARY_LIGHTS }, () => new THREE.Vector4(0, -1, 0, -1)) },
    uPLScale: { value: 1 },
    uPLColor: { value: Array.from({ length: MAX_PRIMARY_LIGHTS }, () => new THREE.Vector4(0, 0, 0, 1)) },
  };
}

/** Fragment-side common: fog, output encoding, shadows, sun, lightmap decode, derivative TBN. */
export const BO2_FRAG_COMMON = /* glsl */ `
uniform float uExposure;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uFogColor;
uniform vec3 uSunFogColor;
uniform vec3 uSunFogDir;
uniform vec4 uFogA;
uniform vec4 uFogB;
uniform highp sampler2DShadow uShadowStatic;
uniform highp sampler2DShadow uShadowDyn;
uniform mat4 uShadowMatrix;
uniform vec4 uShadowParams;
uniform vec4 uDynLightPos;
uniform vec3 uDynLightColor;

uniform vec4 uPLPos[24];
uniform vec4 uPLDir[24];
uniform vec4 uPLColor[24];
// One primary spot/omni light (comworld primaryLights; not in the lightmap per meta / map-hijacked.md open
// items). ESTIMATE attenuation: (1 - (d/r)^2)^2, cone smoothstep(cosOuter, cosInner); no spot shadows.
vec3 bo2PrimaryLight(int i, vec3 wp, vec3 N) {
  vec4 pr = uPLPos[i];
  if (pr.w <= 0.0) return vec3(0.0);
  vec3 L = pr.xyz - wp;
  float d2 = dot(L, L);
  float r2 = pr.w * pr.w;
  if (d2 >= r2) return vec3(0.0);
  float d = sqrt(d2);
  vec3 l = L / max(d, 1e-3);
  float att = 1.0 - d2 / r2;
  att *= att;
  vec4 dr = uPLDir[i];
  float cone = dr.w < -0.5 ? 1.0 : smoothstep(dr.w, uPLColor[i].w, dot(-l, dr.xyz));
  return uPLColor[i].rgb * (att * cone * max(dot(N, l), 0.0));
}
vec3 bo2PrimaryLights(float mask, vec3 wp, vec3 N) {
  int m = int(mask + 0.5) & ~3; // bits 0/1 = none / sun (handled separately)
  vec3 E = vec3(0.0);
  if (m == 0) return E;
  for (int i = 2; i < 24; i++) {
    if ((m & (1 << i)) != 0) E += bo2PrimaryLight(i, wp, N);
  }
  return E;
}

// stand-in for the engine's dynamic lights on explosions / muzzle flashes (ESTIMATE falloff)
vec3 bo2DynLight(vec3 wp, vec3 N) {
  if (uDynLightPos.w <= 0.0) return vec3(0.0);
  vec3 L = uDynLightPos.xyz - wp;
  float d = length(L);
  float att = clamp(1.0 - d / uDynLightPos.w, 0.0, 1.0);
  return uDynLightColor * (att * att * max(dot(N, L / max(d, 1e-3)), 0.0));
}

vec3 bo2ToCod(vec3 t) { return vec3(t.x, -t.z, t.y); }

// IW-style exponential distance fog with exponential height falloff + sun fog (ESTIMATE formula,
// meta.fog values; CPU mirror + tests in lighting.ts fogAmount()).
vec3 bo2Fog(vec3 col, vec3 wp) {
  vec3 d = wp - cameraPosition;
  float dist = length(d);
  float fd = max(0.0, dist - uFogA.x) * uFogA.y;
  float z0 = cameraPosition.y, z1 = wp.y;
  float r0 = min(1.0, exp2(-(z0 - uFogA.z) * uFogA.w));
  float r1 = min(1.0, exp2(-(z1 - uFogA.z) * uFogA.w));
  float dz = z1 - z0;
  float avg = (abs(dz) < 1.0 || z0 < uFogA.z || z1 < uFogA.z) ? 0.5 * (r0 + r1) : (r0 - r1) / (0.69314718 * uFogA.w * dz);
  float cosA = dot(d / max(dist, 1e-3), uSunFogDir);
  float ang = degrees(acos(clamp(cosA, -1.0, 1.0)));
  float sb = clamp(1.0 - (ang - uFogB.z) / max(1e-3, uFogB.w - uFogB.z), 0.0, 1.0);
  float op = mix(uFogB.x, uFogB.y, sb);
  float amt = op * (1.0 - exp2(-fd * avg));
  return mix(col, mix(uFogColor, uSunFogColor, sb), amt);
}

// out = sqrt(col * exposure)  [ps_pimp_shader_lmap: sqrt(... * cb0[20].x)]
vec4 bo2Out(vec3 col, float a) { return vec4(sqrt(max(col * uExposure, vec3(0.0))), a); }

float bo2ShadowTap(highp sampler2DShadow m, vec3 c) { return texture(m, c); }
// 3x3 hardware-PCF taps (each tap is a bilinear 2x2 compare) -> soft 1.5-texel penumbra.
float bo2ShadowMap(highp sampler2DShadow m, vec3 c) {
  vec2 t = uShadowParams.xy;
  float s = 0.0;
  for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) s += texture(m, vec3(c.xy + vec2(float(x), float(y)) * t, c.z));
  return s / 9.0;
}
float bo2SunShadow(vec3 wp, vec3 n) {
  if (uShadowParams.z < 0.5) return 1.0;
  // normal offset (in inches) against acne on grazing surfaces
  vec4 sc = uShadowMatrix * vec4(wp + n * 1.5, 1.0);
  vec3 c = sc.xyz / sc.w;
  if (c.x <= 0.0 || c.x >= 1.0 || c.y <= 0.0 || c.y >= 1.0 || c.z >= 1.0) return 1.0;
  c.z -= 0.0004;
  float s = bo2ShadowMap(uShadowStatic, c);
  if (uShadowParams.w > 0.5) s = min(s, bo2ShadowMap(uShadowDyn, c));
  return s;
}

mat3 bo2TangentFrame(vec3 pos, vec3 N, vec2 uv) {
  vec3 q0 = dFdx(pos); vec3 q1 = dFdy(pos);
  vec2 st0 = dFdx(uv); vec2 st1 = dFdy(uv);
  vec3 q1perp = cross(q1, N); vec3 q0perp = cross(N, q0);
  vec3 T = q1perp * st0.x + q0perp * st1.x;
  vec3 B = q1perp * st0.y + q0perp * st1.y;
  float det = max(dot(T, T), dot(B, B));
  float scale = (det == 0.0) ? 0.0 : inversesqrt(det);
  return mat3(T * scale, B * scale, N);
}
// mapN: tangent-space normal from the texture (glTF convention; derivative frame needs y flipped,
// exactly like three's GLTFLoader does with normalScale.y = -1 for meshes without tangents).
vec3 bo2BumpNormal(vec3 N, vec3 pos, vec2 uv, vec3 mapN) {
  mapN.y = -mapN.y;
  return normalize(bo2TangentFrame(pos, N, uv) * mapN);
}
`;

/** Lightmap decode (packed 3-page image). */
export const BO2_LIGHTMAP = /* glsl */ `
uniform sampler2D uLmPacked;
vec3 bo2Lightmap(vec2 uv1, vec3 Ncod) {
  // pages are 512x1024 stacked vertically in a 512x3072 image; clamp inside a page (bilinear bleed)
  float v = clamp(uv1.y, 0.5 / 1024.0, 1.0 - 0.5 / 1024.0) / 3.0;
  vec4 p0 = texture(uLmPacked, vec2(uv1.x, v));
  vec4 p1 = texture(uLmPacked, vec2(uv1.x, v + 1.0 / 3.0));
  vec4 p2 = texture(uLmPacked, vec2(uv1.x, v + 2.0 / 3.0));
  vec3 A = p0.rgb / (p0.a + 1e-6);
  vec3 B = p1.rgb / (p1.a + 1e-6);
  vec3 dir = p2.rgb * 2.0 - 1.0;
  return A + B * clamp(dot(dir, Ncod), 0.0, 1.0);
}
`;
